import { useEffect, useMemo } from "react";
import { format } from "date-fns";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useApiMutation } from "../../../../common/infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import {
  giftVoucherFormDefaultValues,
  giftVoucherFormSchema,
  IGiftVoucherFormModel,
} from "../models/giftVoucherFormModel";
import { IGiftVoucherModel } from "../models/giftVoucherModels";

interface IUseGiftVoucherFormDialogProps {
  /** Null = create. A voucher = edit, which the API allows only while it is unsold. */
  voucher: IGiftVoucherModel | null;
  visible: boolean;
  onHide: () => void;
}

export const useGiftVoucherFormDialog = ({
  voucher,
  visible,
  onHide,
}: IUseGiftVoucherFormDialogProps) => {
  const isEdit = !!voucher;

  const form = useForm<IGiftVoucherFormModel>({
    defaultValues: giftVoucherFormDefaultValues,
    resolver: yupResolver(giftVoucherFormSchema),
    mode: "onChange",
  });

  // Re-seed on every open so reopening the dialog for a different voucher (or for a
  // create right after an edit) never shows the previous one's values.
  useEffect(() => {
    if (!visible) {
      return;
    }

    form.reset(
      voucher
        ? {
            name: voucher.name,
            amount: voucher.initialAmount,
            validUntil: voucher.validUntil ? new Date(voucher.validUntil) : null,
          }
        : giftVoucherFormDefaultValues,
    );
  }, [visible, voucher, form]);

  const { mutate, isLoading } = useApiMutation();

  const handleSubmit = form.handleSubmit((values: IGiftVoucherFormModel) => {
    mutate(
      {
        endpoint: isEdit
          ? endpointsMetadata.giftVoucher.update
          : endpointsMetadata.giftVoucher.create,
        method: "post",
        globalIsLoading: true,
        // Both endpoints return the voucher object, which has no resultMessage —
        // without an explicit message the toast would read "[object Object]".
        successMessage: isEdit
          ? "Ваучерът е обновен."
          : "Ваучерът е създаден.",
        payload: {
          ...(isEdit ? { id: voucher!.id } : {}),
          name: values.name,
          amount: values.amount,
          // A calendar date, sent as "yyyy-MM-dd". A JS Date would serialize as a UTC
          // instant — local midnight of the 30th in Sofia leaves as "…29T21:00:00Z" — and
          // the voucher would expire a day early.
          validUntil: values.validUntil
            ? format(values.validUntil, "yyyy-MM-dd")
            : null,
        },
        invalidateKeys: [
          [endpointsMetadata.giftVoucher.getAll],
          [endpointsMetadata.giftVoucher.getSellable],
        ],
      },
      {
        onSuccess: () => onHide(),
      },
    );
  });

  // The earliest day the picker offers. Recomputed on every open, so a dialog opened after
  // midnight never still offers yesterday.
  const today = useMemo(() => new Date(new Date().setHours(0, 0, 0, 0)), [visible]);

  return {
    form,
    isLoading,
    isEdit,
    today,
    headerLabel: isEdit ? "Редакция на ваучер" : "Нов подаръчен ваучер",
    confirmLabel: isEdit ? "Запази" : "Създай",
    handleSubmit,
  };
};
